// FILE: src/hooks/rag-hooks.ts
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'; //-tech-debt-react-query;

import {
  fetchItems,
  postItem,
  putItem,
  deleteItem,
} from '../support/queries';

type IdValue = string | number;

type QueryOptions<TData> = Omit<
  UseQueryOptions<TData, Error, TData, any>,
  'queryKey' | 'queryFn'
>;

export type NormalizedList<T = any> = T[] & {
  total_count?: number;
  count?: number;
  items?: T[];
  records?: T[];
  results?: T[];
};

export type RagProductCoverageResponse<T = any> = {
  items: T[];
  totals?: Record<string, any>;
  total_count?: number;
  count?: number;
  [key: string]: any;
};

export const RagCacheKey = {
  All: 'rag',
  Pipeline: 'rag-pipeline',
  PipelineGroup: 'rag-pipeline-group',
  PipelineSlot: 'rag-pipeline-slot',
  PipelinePurpose: 'rag-pipeline-purpose',
  RoutingMapping: 'rag-routing-mapping',
  ProductMapping: 'product-rag-pipeline-mapping',
  IngestionRun: 'rag-pipeline-ingestion-run',
  ProductCoverage: 'rag-product-coverage',
  GtmResolution: 'gtm-rag-resolution',
  GoToMarkets: 'go-to-market',
} as const;

const ENDPOINTS = {
  pipelines: 'db/rag-pipeline',
  pipelineGroups: 'db/rag-pipeline-group',
  pipelineSlots: 'db/rag-pipeline-slot',
  pipelinePurposes: 'db/rag-pipeline-purpose',
  routingMappings: 'db/rag-routing-mapping',
  productMappings: 'db/product-rag-pipeline-mapping',
  ingestionRuns: 'db/rag-pipeline-ingestion-run',
  goToMarkets: 'db/go-to-market',
  gtmResolution: (gtmId: IdValue) => `ai/rag/gtm-resolution/${gtmId}`,
  productCoverage: 'ai/rag/product-coverage',
};

const keys = {
  all: ['rag'],
  pipelines: (p?: any) => ['rag', 'pipelines', p ?? {}],
  pipelineGroups: (p?: any) => ['rag', 'pipeline-groups', p ?? {}],
  pipelineSlots: (p?: any) => ['rag', 'pipeline-slots', p ?? {}],
  pipelinePurposes: (p?: any) => ['rag', 'pipeline-purposes', p ?? {}],
  routingMappings: (p?: any) => ['rag', 'routing-mappings', p ?? {}],
  productMappings: (p?: any) => ['rag', 'product-mappings', p ?? {}],
  ingestionRuns: (p?: any) => ['rag', 'ingestion-runs', p ?? {}],
  goToMarkets: (p?: any) => ['rag', 'go-to-markets', p ?? {}],
  gtmResolution: (id?: IdValue | null) => ['rag', 'gtm-resolution', id ?? null],
  productCoverage: (p?: any) => ['rag', 'product-coverage', p ?? {}],
};

const typedFetchItems = async <T,>(
  endpoint: string,
  params: Record<string, any> = {},
  options: Record<string, any> = {},
): Promise<T> => fetchItems(endpoint, params, options) as Promise<T>;

const typedPostItem = async <TResponse, TPayload>(
  endpoint: string,
  payload: TPayload,
  params: Record<string, any> = {},
): Promise<TResponse> => postItem(endpoint, payload, params) as Promise<TResponse>;

const typedPutItem = async <TResponse, TPayload extends { id: IdValue }>(
  endpoint: string,
  payload: TPayload,
  params: Record<string, any> = {},
): Promise<TResponse> => putItem(endpoint, payload, params) as Promise<TResponse>;


const getDeleteId = (value: any): IdValue => {
  if (typeof value === 'string' || typeof value === 'number') return value;

  const id = value?.id ?? value?.Id;
  if (id === undefined || id === null || id === '') {
    throw new Error('Delete requires an id.');
  }

  return id;
};

export const normalizeItems = <T = any>(data: any): T[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data as T[];
  if (Array.isArray(data.items)) return data.items as T[];
  if (Array.isArray(data.records)) return data.records as T[];
  if (Array.isArray(data.results)) return data.results as T[];
  return [];
};

export const normalizeTotalCount = <T = any>(data: any, rows?: T[]): number => {
  const normalizedRows = rows ?? normalizeItems<T>(data);
  if (!data) return normalizedRows.length;
  if (Array.isArray(data)) return data.length;
  return data.total_count ?? data.count ?? normalizedRows.length;
};

const useInvalidateRag = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['rag'] });
    queryClient.invalidateQueries({ queryKey: [RagCacheKey.Pipeline] });
    queryClient.invalidateQueries({ queryKey: [RagCacheKey.PipelineGroup] });
    queryClient.invalidateQueries({ queryKey: [RagCacheKey.RoutingMapping] });
    queryClient.invalidateQueries({ queryKey: [RagCacheKey.ProductMapping] });
  };
};

export const useFetchRagPipelines = (
  params: Record<string, any> = {},
  options?: QueryOptions<NormalizedList<any>>,
) =>
  useQuery<NormalizedList<any>, Error>({
    queryKey: keys.pipelines(params),
    queryFn: () =>
      typedFetchItems<NormalizedList<any>>(
        ENDPOINTS.pipelines,
        {
          detailed: true,
          depth: 2,
          size: 250,
          'order-by': 'name',
          ...params,
        },
        { pagination: true },
      ),
    ...options,
  });

export const useAddRagPipelineMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<any, Error, any>({
    mutationFn: (payload) =>
      typedPostItem<any, any>(ENDPOINTS.pipelines, payload, { detailed: true, depth: 2 }),
    onSuccess: invalidate,
  });
};

export const useUpdateRagPipelineMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<any, Error, any>({
    mutationFn: (payload) => {
      if (!payload?.id) {
        throw new Error('RAG pipeline update requires an id.');
      }

      return typedPutItem<any, any>(ENDPOINTS.pipelines, payload, { detailed: true, depth: 2 });
    },
    onSuccess: invalidate,
  });
};

export const useDeleteRagPipelineMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<boolean, Error, any>({
    mutationFn: (payload) => {
      const id = getDeleteId(payload);
      return deleteItem(`${ENDPOINTS.pipelines}/${id}/`) as Promise<boolean>;
    },
    onSuccess: invalidate,
  });
};

export const useFetchRagPipelineGroups = (
  params: Record<string, any> = {},
  options?: QueryOptions<NormalizedList<any>>,
) =>
  useQuery<NormalizedList<any>, Error>({
    queryKey: keys.pipelineGroups(params),
    queryFn: () =>
      typedFetchItems<NormalizedList<any>>(
        ENDPOINTS.pipelineGroups,
        { detailed: true, depth: 2, ...params },
        { pagination: true },
      ),
    ...options,
  });

export const useAddRagPipelineGroupMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<any, Error, any>({
    mutationFn: (payload) =>
      typedPostItem<any, any>(ENDPOINTS.pipelineGroups, payload, { detailed: true, depth: 2 }),
    onSuccess: invalidate,
  });
};

export const useUpdateRagPipelineGroupMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<any, Error, any>({
    mutationFn: (payload) => {
      if (!payload?.id) {
        throw new Error('RAG pipeline profile update requires an id.');
      }

      return typedPutItem<any, any>(ENDPOINTS.pipelineGroups, payload, { detailed: true, depth: 2 });
    },
    onSuccess: invalidate,
  });
};

export const useDeleteRagPipelineGroupMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<boolean, Error, any>({
    mutationFn: (payload) => {
      const id = getDeleteId(payload);
      return deleteItem(`${ENDPOINTS.pipelineGroups}/${id}/`) as Promise<boolean>;
    },
    onSuccess: invalidate,
  });
};

export const useFetchRagPipelinePurposes = (
  params: Record<string, any> = {},
  options?: QueryOptions<NormalizedList<any>>,
) =>
  useQuery<NormalizedList<any>, Error>({
    queryKey: keys.pipelinePurposes(params),
    queryFn: () =>
      typedFetchItems<NormalizedList<any>>(
        ENDPOINTS.pipelinePurposes,
        {
          detailed: true,
          size: 250,
          'order-by': 'sort_order',
          ...params,
        },
        { pagination: true },
      ),
    ...options,
  });

export const useFetchRagRoutingMappings = (
  params: Record<string, any> = {},
  options?: QueryOptions<NormalizedList<any>>,
) =>
  useQuery<NormalizedList<any>, Error>({
    queryKey: keys.routingMappings(params),
    queryFn: () =>
      typedFetchItems<NormalizedList<any>>(
        ENDPOINTS.routingMappings,
        {
          detailed: true,
          depth: 2,
          size: 25,
          'order-by': 'priority',
          ...params,
        },
        { pagination: true },
      ),
    ...options,
  });

export const useAddRagRoutingMappingMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<any, Error, any>({
    mutationFn: (payload) =>
      typedPostItem<any, any>(ENDPOINTS.routingMappings, payload, { detailed: true, depth: 2 }),
    onSuccess: invalidate,
  });
};

export const useUpdateRagRoutingMappingMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<any, Error, any>({
    mutationFn: (payload) => {
      if (!payload?.id) {
        throw new Error('RAG routing mapping update requires an id.');
      }

      return typedPutItem<any, any>(ENDPOINTS.routingMappings, payload, { detailed: true, depth: 2 });
    },
    onSuccess: invalidate,
  });
};

export const useDeleteRagRoutingMappingMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<boolean, Error, any>({
    mutationFn: (payload) => {
      const id = getDeleteId(payload);
      return deleteItem(`${ENDPOINTS.routingMappings}/${id}/`) as Promise<boolean>;
    },
    onSuccess: invalidate,
  });
};

export const useFetchProductRagPipelineMappings = (
  params: Record<string, any> = {},
  options?: QueryOptions<NormalizedList<any>>,
) =>
  useQuery<NormalizedList<any>, Error>({
    queryKey: keys.productMappings(params),
    queryFn: () =>
      typedFetchItems<NormalizedList<any>>(
        ENDPOINTS.productMappings,
        { detailed: true, depth: 2, ...params },
        { pagination: true },
      ),
    ...options,
  });

export const useAddProductRagPipelineMappingMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<any, Error, any>({
    mutationFn: (payload) =>
      typedPostItem<any, any>(ENDPOINTS.productMappings, payload, { detailed: true, depth: 2 }),
    onSuccess: invalidate,
  });
};

export const useUpdateProductRagPipelineMappingMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<any, Error, any>({
    mutationFn: (payload) => {
      if (!payload?.id) {
        throw new Error('Product RAG pipeline mapping update requires an id.');
      }

      return typedPutItem<any, any>(ENDPOINTS.productMappings, payload, { detailed: true, depth: 2 });
    },
    onSuccess: invalidate,
  });
};

export const useDeleteProductRagPipelineMappingMutation = () => {
  const invalidate = useInvalidateRag();

  return useMutation<boolean, Error, any>({
    mutationFn: (payload) => {
      const id = getDeleteId(payload);
      return deleteItem(`${ENDPOINTS.productMappings}/${id}/`) as Promise<boolean>;
    },
    onSuccess: invalidate,
  });
};

export const useFetchRagPipelineIngestionRuns = (
  params: Record<string, any> = {},
  options?: QueryOptions<NormalizedList<any>>,
) =>
  useQuery<NormalizedList<any>, Error>({
    queryKey: keys.ingestionRuns(params),
    queryFn: () =>
      typedFetchItems<NormalizedList<any>>(
        ENDPOINTS.ingestionRuns,
        {
          detailed: true,
          depth: 2,
          size: 25,
          'order-by': '-created',
          ...params,
        },
        { pagination: true },
      ),
    ...options,
  });

export const useFetchAllGoToMarkets = (
  params: Record<string, any> = {},
  options?: QueryOptions<NormalizedList<any>>,
) =>
  useQuery<NormalizedList<any>, Error>({
    queryKey: keys.goToMarkets(params),
    queryFn: () =>
      typedFetchItems<NormalizedList<any>>(
        ENDPOINTS.goToMarkets,
        {
          detailed: true,
          depth: 1,
          size: 250,
          'order-by': 'name',
          ...params,
        },
        { pagination: true },
      ),
    ...options,
  });

export const useFetchGtmRagResolution = (
  gtmId?: IdValue | null,
  options?: QueryOptions<any>,
) =>
  useQuery<any, Error>({
    queryKey: keys.gtmResolution(gtmId),
    queryFn: () =>
      typedFetchItems<any>(
        ENDPOINTS.gtmResolution(gtmId as IdValue),
        {},
        { includePageSize: false },
      ),
    ...{
        enabled: Boolean(gtmId),
        ...options,
      },
  });

export const useFetchRagProductCoverage = (
  params: Record<string, any> = {},
  options?: QueryOptions<RagProductCoverageResponse<any>>,
) =>
  useQuery<RagProductCoverageResponse<any>, Error>({
    queryKey: keys.productCoverage(params),
    queryFn: async () => {
      const data = await typedFetchItems<any>(
        ENDPOINTS.productCoverage,
        params,
        {
          includePageSize: false,
          pagination: true,
        },
      );

      if (Array.isArray(data)) {
        return {
          items: data,
          totals: {},
          count: data.length,
          total_count: data.length,
        };
      }

      return {
        ...data,
        items: normalizeItems(data),
        totals: data?.totals ?? {},
      };
    },
    ...options,
  });
